import React, { useState, useRef, useEffect } from 'react';
import { Info, CloudUpload, X, ChevronDown, Search, CircleCheck } from 'lucide-react';
import { useHistory, useParams } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import apiHelper from '../../apiHelper';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import MultiSelectSearch from '../../utils/MultiSelectSearch.js';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';

export default function EditBrand() {
  const history = useHistory();
  const { id } = useParams();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [validationError, setValidationError] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [alertType, setAlertType] = useState("success"); // "success" or "error"
  const saveRef = useRef(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [showItemAlert, setShowItemAlert] = useState(false);
  const [alert, setAlert] = useState(null);
  const [isParentBrandOpen, setIsParentBrandOpen] = useState(false);
  const [parentBrandSearch, setParentBrandSearch] = useState('');
  const parentBrandRef = useRef(null);
  const [selectedParentBrands, setSelectedParentBrands] = useState([]);
  const [activeTab] = useState("Edit Brand");
  const breadcrumbs = ["Product", "Brand", activeTab];
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [imageError, setImageError] = useState(null);
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
  const [dataLoaded, setDataLoaded] = useState(false);


  const [formData, setFormData] = useState({
    id: '',
    brand_name: '',
    prefix: '',
    parent_brand: '',
    original_logo: null,
    status: '1',
    notes: '',
  });

  const [formErrors, setFormErrors] = useState({
    id: '',
    brand_name: '',
    prefix: '',
    parent_brand: '',
    original_logo: null,
    status: '1',
    notes: '',
  });

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      original_logo: uploadedImage
    }));
  }, [uploadedImage]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Validate file type
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
        return;
      }

      // Valid file
      setUploadedImage(file);
      setImageError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadedImage) {
      // Prevent dropping a new file if one already exists
      return;
    }


    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];

      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setImageError('Upload failed — Only JPEG/PNG files below 1MB are supported');
        return;
      }

      setUploadedImage(file);
      setImageError(null);
      e.dataTransfer.clearData();
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleBrowseClick = () => {
    if (uploadedImage) {
      // Prevent selecting new file if one is already uploaded
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }

  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const [allBrands, setAllBrands] = useState([]);


  const toggleParentBrand = () => {
    setIsParentBrandOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isParentBrandOpen && parentBrandRef.current && !parentBrandRef.current.contains(e.target)) {
        setIsParentBrandOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isParentBrandOpen]);

  const filteredParentBrand = allBrands.filter((d) =>
    d.brand_name.toLowerCase().includes(parentBrandSearch.trim().toLowerCase())
  );

  const handleParentBrandSelect = (brand) => {
    const updatedParents = [...selectedParentBrands];
    if (!updatedParents.some((b) => b.brand_id === brand.brand_id)) {
      updatedParents.push(brand);
    }
    setSelectedParentBrands(updatedParents);

    setFormData((prev) => ({
      ...prev,
      parent_brand: updatedParents.map((b) => b.brand_id), // array of IDs
    }));

    setParentBrandSearch('');
    setIsParentBrandOpen(false);
  };

  const removeParentBrand = (id) => {
    const updated = selectedParentBrands.filter((b) => b.brand_id !== id);
    setSelectedParentBrands(updated);

    setFormData((prev) => ({
      ...prev,
      parent_brand: updated.map((b) => b.brand_id),
    }));
  };

  const handleSave = async () => {
    setDisabled(true);
    setIsDirty(false);
    let form_errors = {};
    let is_error = false;
    const newErrors = {};

    // Validate Brand Name
    if (!formData.brand_name?.trim()) {
      form_errors.brand_name = "Brand Name is required";
    }
    if (formData.id && formData.id.length > 50) {
      form_errors.id = "";
    }

    // Local duplicate checks
    const dupName = validateLocalDuplicate("brand_name", formData.brand_name || "");
    const dupId = validateLocalDuplicate("id", formData.id || "");

    // Add duplicate messages without overwriting required error
    if (!form_errors.brand_name && dupName) {
      form_errors.brand_name = "Brand Name already exists";
    }

    if (dupId) {
      form_errors.id = "Brand ID already exists";
    }

    // Now check ALL errors at once
    if (Object.keys(form_errors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...form_errors }));
      showAlert("error", "update", "Brand");
      setDisabled(false);
      return;
    }


    let postFormData = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "parent_brand" && Array.isArray(formData.parent_brand)) {
        formData.parent_brand.forEach((id) => postFormData.append("parent_brand", id));
      } else if (key !== "original_logo" || uploadedImage) {
        postFormData.append(key, formData[key]);
      }
    });

    if (uploadedImage) {
      postFormData.append("original_logo", uploadedImage);
      postFormData.append("cropped_logo", uploadedImage);
    }

    const response = await apiHelper(`inventory/brands/update/${id}/`, "POST", postFormData, "multipart/form-data");
    setDisabled(false);
    setValidationError([]);

    if (response && response.ok) {
      showAlert("success", "update", "Brand"); // green banner
      setIsDirty(false);
      history.push("/brand", { success: " Brand Saved successfully" });
      return;
    }
    if (response?.status === 400) {
      const response_errors = await response.json();
      let newErrors = { ...formErrors };
      let errorMessages = [];

      for (let key in response_errors) {
        newErrors[key] = response_errors[key][0]; // first error message
        errorMessages.push(`${key}: ${response_errors[key][0]}`);
      }

      setFormErrors(newErrors);
      showAlert("error", "create", "Brand");
      setValidationError(errorMessages);
      return;
    }
    showAlert("error", "update", "Brand");
  };
  useEffect(() => {
    async function fetchBrandsData() {
      const response = await apiHelper('inventory/brands/?is_archived=false', 'GET');
      if (response && response.ok) {
        const result = await response.json();
        const activeBrands = result.filter(b => b.status === true);
        setAllBrands(activeBrands);
        return result;
      }
      return null;
    }

    async function fetchBrandData() {
      const res = await apiHelper(`inventory/brands/show/${id}/`, 'GET');
      if (res.ok) {
        const result = await res.json();
        setFormData({
          brand_id: result.brand_id,
          id: result.id || '',
          brand_name: result.brand_name || '',
          prefix: result.prefix || '',
          parent_brand: result.parent_brand || [],
          original_logo: result.original_logo_url || null,
          status: result.status ? '1' : '0',
          notes: result.notes || '',
        });

        if (result.parent_brand) {
          setSelectedParentBrands([
            {
              brand_id: result.parent_brand,
              brand_name: result.parent_name || '',
            },
          ]);
        }
        return result;
      }
      return null;
    }

    (async () => {
      await fetchBrandsData();
      await fetchBrandData();
      setDataLoaded(true); // validation may run now
    })();
  }, [id]);

  const showAlert = (type, action, recordName) => {
    setAlert({ type, action, recordName });
    setShowItemAlert(true);
    setTimeout(() => setShowItemAlert(false), 5000);
  };

  const handleSaveConfirm = () => {
    setSaveModalOpen(false);
    showAlert("Brand updated successfully");
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (saveRef.current && !saveRef.current.contains(event.target)) {
        setSaveModalOpen(false);
      }
    }

    if (saveModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [saveModalOpen]);
  const handleDiscard = () => {
    setDiscardModalOpen(true);
  };

  const handleDiscardConfirm = (confirm) => {
    if (confirm) {
      console.log("🟠 Discard confirmed, navigating to brand with state...");
      setIsDiscarding(true);
      setIsDirty(false);
      setDiscardModalOpen(false);

      setTimeout(() => {
        console.log("🟢 Navigating with success state");
        history.push("/brand", { success: "Brand Discarded Successfully" });
      }, 100);
    } else {
      setDiscardModalOpen(false);
    }
  };
  const handleChangeWithDirty = (e) => {
    setIsDirty(true);
    handleChange(e);
  };

  // const validateLocalDuplicate = (field, value) => {
  //   if (!value?.toString?.().trim()) return false;

  //   const normalized = value.toString().trim().toLowerCase();
  //   let duplicateFound = false;

  //   if (field === "brand_name") {
  //     duplicateFound = allBrands.some((b) => {
  //       const bName = b?.brand_name?.toString?.().trim?.().toLowerCase?.();
  //       const bId = b?.id?.toString?.().trim?.().toLowerCase?.();
  //       const currentId = formData?.id?.toString?.().trim?.().toLowerCase?.();
  //       // Exclude same record
  //       return bName === normalized && bId && currentId && bId !== currentId;
  //     });
  //   }

  //   if (field === "id") {
  //     duplicateFound = allBrands.some((b) => {
  //       const recordId = b?.id?.toString?.().trim?.().toLowerCase?.();
  //       const currentId = formData?.id?.toString?.().trim?.().toLowerCase?.();
  //       return recordId && recordId === normalized && recordId !== currentId;
  //     });
  //   }

  //   return duplicateFound;
  // };



  const validateLocalDuplicate = (field, value) => {
    if (!value?.toString?.().trim()) return false;

    const normalized = value.toString().trim().toLowerCase();
    const currentBrandId = formData.brand_id?.toString()?.trim()?.toLowerCase();

    if (field === "brand_name") {
      return allBrands.some((b) => {
        const bName = b.brand_name?.toString().trim().toLowerCase();
        const bBrandId = b.brand_id?.toString()?.trim()?.toLowerCase();

        const isSameRecord = bBrandId === currentBrandId;

        return bName === normalized && !isSameRecord;
      });
    }

    if (field === "id") {
      return allBrands.some((b) => {
        const bId = b.id?.toString().trim().toLowerCase();
        const bBrandId = b.brand_id?.toString()?.trim()?.toLowerCase();

        const isSameRecord = bBrandId === currentBrandId;

        return bId === normalized && !isSameRecord;
      });
    }

    return false;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };
  const handleFieldBlur = (e) => {

    const { name, value } = e.target;
    const trimmed = value?.toString?.().trim?.() ?? "";

    setFormErrors((prev) => {
      const newErrors = { ...prev };

      // This is the TRUE ID of the record from backend
      const currentBrandId = formData.brand_id?.toString().trim().toLowerCase();

      // --- BRAND NAME BLUR VALIDATION ---
      if (name === "brand_name") {
        if (!trimmed) {
          newErrors.brand_name = "Brand Name is required";
        } else {
          const normalizedName = trimmed.toLowerCase();

          const isDupName = allBrands.some((b) => {
            const bName = b.brand_name?.trim()?.toLowerCase();
            const bBrandId = b.brand_id?.toString()?.trim()?.toLowerCase();

            const isSameRecord = bBrandId === currentBrandId;

            return bName === normalizedName && !isSameRecord;
          });

          newErrors.brand_name = isDupName ? "Brand Name already exists" : "";
        }

        return { ...newErrors };
      }

      // --- ID BLUR VALIDATION ---
      if (name === "id") {
        if (!trimmed) {
          newErrors.id = "";
        } else {
          const normalizedId = trimmed.toLowerCase();

          const isDupId = allBrands.some((b) => {
            const bId = b.id?.toString()?.trim()?.toLowerCase();
            const bBrandId = b.brand_id?.toString()?.trim()?.toLowerCase();

            const isSameRecord = bBrandId === currentBrandId;

            return bId === normalizedId && !isSameRecord;
          });

          newErrors.id = isDupId ? "Brand ID already exists" : "";
        }

        return { ...newErrors };
      }

      return newErrors;
    });
  };

  useEffect(() => {
    const navEntries = performance.getEntriesByType("navigation");
    const type = navEntries[0]?.type;
  }, []);


  console.log("formData:", formData);
  return (
    <>
      <div className="company-body main-company-body-inner new-look">
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <span
                key={index}
                className={`breadcrumb-item ${crumb === "Brand" ? "clickable" : ""}`}
                onClick={() => {
                  if (crumb === "Brand") {
                    history.push("/brand");
                  }
                }}
                style={{ cursor: crumb === "Brand" ? "pointer" : "default" }}
              >
                {crumb}
                {!isLast ? "  " : ""}
              </span>
            );
          })}
        </div>
        {showItemAlert && alert && (
          <AlertNotification
            type={alert.type}
            action={alert.action}
            recordName={alert.recordName}
            onClose={() => setShowItemAlert(false)}
          />
        )}

        <div className='edit-organisation-info'>
          <div className='edit-organisation-left'>
            <h1>Edit Brand</h1>
          </div>
          <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
            <button className="dark-button" style={{
              display: 'flex',
              height: '34px',
              minWidth: '100px',
              padding: '8px',
              justifyContent: 'center',
              alignContent: 'center',
              gap: '10px',
              borderRadius: '8px',
            }} type='button' onClick={handleDiscard}>
              Discard
            </button>
            {/* Common Confirmation Modal */}
            <DiscardConfirmationModal
              isOpen={discardModalOpen}
              onCancel={() => setDiscardModalOpen(false)}
              onConfirm={handleDiscardConfirm}
              entityName="Brand"
            />
            <button className="dark-border" style={{
              display: 'flex',
              height: '34px',
              minWidth: '100px',
              padding: '8px',
              justifyContent: 'center',
              alignContent: 'center',
              gap: '10px',
              borderRadius: '8px',
              boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
            }} type='button' onClick={handleSave}>
              Save
            </button>
          </div>
        </div>

        <div className="info-section">
          <div className="info-card new-info-overflow-auto">
            <div className="info-header">
              <h4 className="info-title">Basic Info</h4>
            </div>
            <div className="info-content d-block">
              <div className='grid-info-adding'>
                <div className='row gappingcls-added'>
                  <div className='col-lg-6 col-md-6 col-12'>
                    <div className="info-inner">
                      <label htmlFor="brandName" className="form-label d-flex align-items-center gap-1">
                        Brand Name <span className='error-color'>*</span>
                        {/* <Tooltip content="Brand Name">
                          <Info size={16} className="info-icon" />
                        </Tooltip> */}
                      </label>
                      <input
                        type="text"
                        className="form-control common-input-all"
                        id="brandName"
                        // placeholder="Brand Name"
                        name="brand_name"
                        value={formData.brand_name}
                        onChange={handleFieldChange}
                        onBlur={handleFieldBlur}
                        style={formErrors.brand_name ? { borderColor: '#dc3545' } : {}}
                      />
                      {formErrors.brand_name && <p style={{
                        fontSize: '12px',
                        color: "#DC3545",
                        fontFamily: "Montserrat",
                        fontStyle: 'normal',
                        fontWeight: '400px',
                        lineHeight: 'normal',
                        marginTop: '5px'
                      }}>{formErrors.brand_name}</p>}

                    </div>
                  </div>
                  <div className='col-lg-6 col-md-6 col-12'>
                    <div className="info-inner">
                      <label htmlFor="status" className="form-label">
                        Status <span className='error-color'>*</span>
                      </label>
                      <div className='flexing-type-radio'>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="status"
                            id="flexRadioDefault1"
                            checked={formData.status === '1'}
                            value="1"
                            onChange={handleFieldChange}
                          />
                          <label className="form-check-label" htmlFor="flexRadioDefault1" style={{ fontWeight: 'normal', fontSize: '14px' }}>
                            Active
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="status"
                            id="flexRadioDefault2"
                            checked={formData.status === '0'}
                            value="0"
                            onChange={handleFieldChange}
                          />
                          <label className="form-check-label" htmlFor="flexRadioDefault2" style={{ fontWeight: 'normal', fontSize: '14px' }}>
                            Inactive
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='row gappingcls-added'>
                  <div className='col-lg-6 col-md-6 col-12'>
                    <div className="info-inner">
                      <label htmlFor="brandId" className="form-label d-flex align-items-center gap-2">
                        ID
                        {/* <Tooltip content="A shorter, human-friendly label for your brand">
                          <Info size={16} className="info-icon" />
                        </Tooltip> */}
                      </label>
                      <CharacterLimitedTextarea
                        id="brandId"
                        // placeholder="Brand ID"
                        name="id"
                        value={formData.id}
                        onChange={handleChangeWithDirty}
                        onBlur={handleFieldBlur}
                        maxLength={50}
                        counterPosition="end" // or "below" if you prefer below the input
                        error={formErrors.id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="info-card new-info-overflow-auto">
            <div className="info-header">
              <h4 className="info-title">Business Info</h4>
            </div>
            <div className="info-content d-block">
              <div className='grid-info-adding'>
                <div className='row gappingcls-added'>
                  <div className='col-lg-6 col-md-6 col-12'>
                    <div className="info-inner" style={{ position: 'relative' }}>
                      <MultiSelectSearch
                        label="Parent Brand"
                        tooltip={<Tooltip title="Parent Brand" body="The main brand this brand belongs under if any. Example: Redmi’s parent brand is Xiaomi"><Info size={16} className="info-icon" /></Tooltip>}
                        options={allBrands} // full list of brands
                        selected={selectedParentBrands} // pre-selected ones
                        setSelected={(updated) => {
                          setSelectedParentBrands(updated);
                          setFormData((prev) => ({
                            ...prev,
                            parent_brand: updated.map((b) => b.brand_id),
                          }));
                        }}
                        multiple={false}
                        keysToSearch={["brand_name"]}
                        idKey="brand_id"
                        displayKey="brand_name"
                        logoKey="original_logo_url"
                        // placeholder="Search Parent Brand..."
                        subTitleKey="parent_name" // shows parent brand
                        currentBrandId={id}
                      />
                    </div>
                  </div>

                  <div className='col-lg-6 col-md-6 col-12'>
                    <div className="info-inner">
                      <label htmlFor="prefix" className="form-label d-flex align-items-center gap-1">
                        Prefix
                        <Tooltip
                          title="Prefix"
                          body="A short code for the brand, used in automating SKU generation">
                          <Info size={16} className="info-icon" />
                        </Tooltip>
                      </label>
                      <input
                        type="text"
                        className="form-control common-input-all"
                        id="prefix"
                        // placeholder="Prefix"
                        name="prefix"
                        value={formData.prefix}
                        onChange={handleChangeWithDirty}
                      />
                    </div>
                  </div>
                </div>

                <div className='row gappingcls-added'>
                  <div className='col-lg-6 col-md-6 col-12'>
                    <div className="info-inner">
                      <label className="form-label d-flex align-items-center gap-2">
                        Logo
                        <Tooltip
                          title="Logo"
                          body="Upload the brand’s logo in a square (1:1) aspect ratio for best results. Maximum file size: 1MB. Only JPEG and PNG formats are supported."
                        >
                          <Info size={16} className="info-icon" />
                        </Tooltip>                      </label>
                      <div
                        className="upload-container"
                        onClick={handleBrowseClick}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        style={{
                          border: '2px dashed #ccc',
                          borderRadius: '4px',
                          padding: '3rem 1.5rem',
                          textAlign: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          width: '100%',
                          backgroundColor: isDragging ? '#CFE2FF' : '#fff',
                          borderColor: isDragging ? '#0D6EFD' : '#ccc',
                          transition: 'background-color 0.3s ease',
                        }}
                      >
                        {(uploadedImage || formData.original_logo) ? (
                          <div style={{ display: 'inline-block' }}>
                            <img
                              src={uploadedImage ? URL.createObjectURL(uploadedImage) : formData.original_logo}
                              alt="Preview"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '150px',
                                objectFit: 'contain',
                                borderRadius: '8px',
                              }}
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage();
                              }}
                              style={{
                                position: 'absolute',
                                top: '6px',
                                right: '10px',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '0',
                                cursor: 'pointer',
                                fontSize: '34px',
                                padding: 0,
                                lineHeight: '30px',
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="upload-placeholder">
                            <CloudUpload />
                            <p style={{ margin: 0, color: '#555', fontSize: '0.9rem' }}>
                              Drag your image here or{' '}
                              <span style={{ color: '#007bff', textDecoration: 'underline' }}>
                                Browse
                              </span>
                            </p>
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/jpeg, image/png"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          onChange={handleImageChange}
                        />
                      </div>
                      {imageError && (
                        <p style={{ color: 'red', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                          {imageError}
                        </p>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <h4 className="info-title">Notes</h4>
            </div>
            <div className="info-content">
              <div className="info-inner common-textarea">
                {/* <label htmlFor="exampleFormControlTextarea1" className="form-label">
                  Note
                </label> */}
                <textarea
                  className="form-control w-100 common-textarea-all"
                  id="exampleFormControlTextarea1"
                  rows="3"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChangeWithDirty}
                  style={{ border: '1px solid #6C757D' }}
                ></textarea>
              </div>
            </div>
          </div>
          <UnsavedChangesPrompt isDirty={isDirty && !isDiscarding} entityName="Brand" />
        </div>
        {saveModalOpen && (
          <>
            <div className="drawer-overlay" />
            <div
              className="entity-drawer modal-popup-cls"
              ref={saveRef}
              style={{
                maxWidth: 650,
                margin: "0 auto",
                background: "white",
                borderRadius: 12,
                zIndex: 1050,
                padding: 24,
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                height: "auto",
                right: "auto",
                overflow: "inherit",
              }}
            >
              <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                  <X size={20} />
                </button>
              </div>
              <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
              <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
              <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                <button className="btn-save" onClick={handleSaveConfirm}>
                  OK
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}